import { FileStore } from "#/api/fileStore.type";
import { Spinner } from "@heroui/react";
import { useEffect, useState } from "react";
import { useFileUploadContext } from "../../hooks/FileUploadContext";
import Typography from "../shared/typography";
import { cn, getMimeTypeFromExtension } from "../utls/utils";
import { ChatInput } from "./chat-input";
import { ImageCarousel } from "./image-carousel";
import { UploadImageInput } from "./upload-image-input";

interface InteractiveChatBoxProps {
  isDisabled?: boolean;
  mode?: "stop" | "submit";
  onSubmit: (message: string, files: FileStore[]) => void;

  value?: string;
  onChange?: (message: string) => void;
}

export function InteractiveChatBox({
  isDisabled,
  mode = "submit",
  onSubmit,
  value,
  onChange,
}: InteractiveChatBoxProps) {
  const {
    files,
    setFiles,
    fileUploading,
    uploadFilesForChat,
    fileUploadResponse,
    handleRemoveImage,
  } = useFileUploadContext();

  const [messageToIncludeWithFiles, setMessageToIncludeWithFiles] =
    useState("");

  const handleUploadLocally = (newFiles: File[]) => {
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const handleSubmit = (message: string) => {
    if (files && files.length > 0) {
      uploadFilesForChat({
        files,
      });
      setMessageToIncludeWithFiles(message);
    } else {
      onSubmit(message, []);
    }
  };

  useEffect(() => {
    if (!fileUploading && fileUploadResponse && files && files.length > 0) {
      onSubmit(messageToIncludeWithFiles, fileUploadResponse);
      setFiles([]);
      if (value) {
        onChange?.("");
      }
    }
  }, [fileUploading, fileUploadResponse, files]);

  return (
    <div
      data-testid="interactive-chat-box"
      className="flex flex-col gap-[10px] w-full"
    >
      {files.length > 0 && !fileUploading && (
        <ImageCarousel
          size="small"
          images={files.map((file) => ({
            url: URL.createObjectURL(file),
            fileName: file.name,
            fileType: getMimeTypeFromExtension(file.name),
          }))}
          onRemove={handleRemoveImage}
        />
      )}
      {fileUploading && (
        <div className="flex items-center justify-center p-2 bg-beige-100 rounded-lg">
          <div className="flex items-center gap-4">
            <Typography variant="body2" className="text-terracotta-700">
              Uploading files
            </Typography>
            <Spinner
              color="primary"
              className="w-4 h-4 text-terracotta-500"
              variant="wave"
            />
          </div>
        </div>
      )}

      <div
        className={cn(
          "flex items-center gap-1",
          "bg-white border rounded-lg py-1 px-0", // Removed horizontal padding completely
          "transition-colors duration-200",
          "border-beige-300 hover:border-terracotta-300 focus-within:border-terracotta-400",
          "shadow-sm"
        )}
      >
        <UploadImageInput
          onUpload={handleUploadLocally}
          accept=".jpg,.jpeg,.png,.gif,.webp, .pdf"
        />
        <ChatInput
          disabled={isDisabled || fileUploading}
          button={mode}
          onChange={onChange}
          onSubmit={handleSubmit}
          value={value}
          className="py-[10px]"
          buttonClassName="py-[10px]"
        />
      </div>
    </div>
  );
}
